import type { FC, MouseEvent, ReactNode } from "react";
import type { TFunction } from "i18next";
import { ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import DefinitionTooltip from "src/components/common/DefinitionTooltip/DefinitionTooltip";

export const REPORT_KEY = "socialListingAgent.listeningReport";

type Tone = "pos" | "neg" | "blue";

const joinClasses = (...classes: (string | false | undefined)[]) => classes.filter(Boolean).join(" ");

interface ReportTagProps {
  tone?: Tone;
  small?: boolean;
  children: ReactNode;
}

export const ReportTag: FC<ReportTagProps> = ({ tone, small = false, children }) => (
  <span className={joinClasses("br__tag", tone && `br__tag--${tone}`, small && "br__tag--sm")}>
    {children}
  </span>
);

interface ReportBadgeProps {
  tone: Tone | "neu";
  children: ReactNode;
}

export const ReportBadge: FC<ReportBadgeProps> = ({ tone, children }) => (
  <span className={`br__badge br__badge--${tone}`}>{children}</span>
);

const SPAN_CLASS_BY_COUNT: Record<number, string> = { 1: "br__c12", 2: "br__c6" };

export const getSpanClass = (count: number): string => SPAN_CLASS_BY_COUNT[count] ?? "br__c4";

export const getBrandScope = (
  t: TFunction,
  primary: string,
  competitor?: string,
): { label: string; tone?: Tone } =>
  competitor
    ? {
        label: t(`${REPORT_KEY}.scope.versus`, "{{primary}} vs {{competitor}}", { primary, competitor }),
        tone: "blue",
      }
    : { label: t(`${REPORT_KEY}.scope.only`, "{{primary}} only", { primary }) };

export const HelpDot: FC<{ text: string }> = ({ text }) => (
  <DefinitionTooltip label={text} info={{ description: text }} />
);

interface CardTitleProps {
  help?: string;
  children: ReactNode;
}

export const CardTitle: FC<CardTitleProps> = ({ help, children }) => (
  <h2 className="br__card-title">
    {children}
    {help && <HelpDot text={help} />}
  </h2>
);

interface SectionTitleProps {
  scope?: { label: string; tone?: Tone };
  children: ReactNode;
}

export const SectionTitle: FC<SectionTitleProps> = ({ scope, children }) => (
  <h2 className="br__section-title">
    {children}
    {scope && (
      <span className="br__scope">
        <ReportTag small tone={scope.tone}>
          {scope.label}
        </ReportTag>
      </span>
    )}
  </h2>
);

interface ViewPostLinkProps {
  url?: string;
  small?: boolean;
}

export const ViewPostLink: FC<ViewPostLinkProps> = ({ url, small = false }) => {
  const { t } = useTranslation();
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!url) {
      event.preventDefault();
    }
  };

  return (
    <a
      className={joinClasses("br__btn-view", small && "br__btn-view--sm")}
      href={url ?? "#"}
      target={url ? "_blank" : undefined}
      rel={url ? "noopener noreferrer" : undefined}
      aria-disabled={!url}
      onClick={handleClick}
    >
      <ExternalLink className="br-ic" aria-hidden />
      {t(`${REPORT_KEY}.viewPost`, "View post")}
    </a>
  );
};
